import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Checkbox } from '../components/Checkbox'
import { FormNavigation } from '../components/FormNavigation'
import { yousignService, getApiErrorCode, getApiErrorMessage, getApiErrorStatus } from '../services/api'
import { reportError } from '../services/errorReporter'
import { Phone } from 'lucide-react'
import { useOnboardingStore } from '../store/onboardingStore'
import { useTestMode } from '../context/TestModeContext'
import { validatePhoneNumber } from '../utils/validation'
import { ensureSignatureRequest } from '../utils/ensureSignatureRequest'
import { toast } from 'sonner'
import { logger } from '../utils/logger'
import type { StepProps } from '../types'

type Phase = 'confirm-phone' | 'enter-otp'

/** Clear the stale SR and send the patient back to the summary step (creates a fresh SR) */
function recoverFromStaleSignatureRequest() {
  useOnboardingStore.setState({
    yousignRequestId: null,
    yousignSignerId: null,
    yousignDocuments: [],
    yousignDataHash: null,
    yousignPhoneNumber: null,
    currentStep: 6,
  })
  setTimeout(() => { window.location.href = window.location.pathname + window.location.search }, 1500)
}

export function Step9PhoneVerification({ onNext, onBack }: StepProps) {
  const { t } = useTranslation()
  const isTestMode = useTestMode()
  const {
    phoneNumber,
    setPhoneNumber,
    personalData,
    patientNumber,
    yousignRequestId,
    yousignSignerId,
    yousignPhoneNumber,
    agreedToTerms,
    setAgreedToTerms,
  } = useOnboardingStore()

  const [phase, setPhase] = useState<Phase>('confirm-phone')
  const [editablePhone, setEditablePhone] = useState(phoneNumber)
  const [phoneError, setPhoneError] = useState<string | undefined>()
  const [code, setCode] = useState<string[]>(Array(6).fill(''))
  const [isLoading, setIsLoading] = useState(false)
  // Resend cooldown: without it patients tap "Erneut senden" repeatedly and
  // hit both our limiter and Yousign's 429 (48 such errors in 90 days).
  const [resendCooldown, setResendCooldown] = useState(0)
  useEffect(() => {
    if (resendCooldown <= 0) return
    const id = setTimeout(() => setResendCooldown(c => c - 1), 1000)
    return () => clearTimeout(id)
  }, [resendCooldown])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Same validator as step 2 — the two steps previously disagreed on what
  // a valid phone number is (audit finding M3)
  const isValidPhone = validatePhoneNumber(editablePhone).valid
  const isOtpComplete = code.every(digit => digit !== '')

  useEffect(() => {
    if (phase === 'enter-otp') {
      inputRefs.current[0]?.focus()
    }
  }, [phase])

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditablePhone(e.target.value)
    if (phoneError) setPhoneError(undefined)
  }

  const handlePhoneBlur = () => {
    if (editablePhone && !isValidPhone) {
      setPhoneError(t('step10.phoneValidation'))
    } else {
      setPhoneError(undefined)
    }
  }

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValidPhone || !agreedToTerms) return

    // In test mode, skip OTP and go straight to next step
    if (isTestMode) {
      toast.success(t('step10.testModeSkip'))
      onNext()
      return
    }

    setIsLoading(true)
    try {
      // Update phone number in store if changed
      if (editablePhone !== phoneNumber) {
        setPhoneNumber(editablePhone)
      }

      // The OTP SMS goes to the number embedded in the signature request at
      // creation time — a corrected number here MUST recreate the SR, or
      // Yousign keeps texting the old number forever (P13812 incident).
      // ensureSignatureRequest is a no-op when nothing changed.
      try {
        const srState = await ensureSignatureRequest()
        if (srState !== 'unchanged') {
          logger.log('[Step10] Signature request recreated before OTP send:', srState)
        }
      } catch (ensureError: unknown) {
        logger.error('[Step10] Failed to (re)create signature request:', ensureError)
        const status = getApiErrorStatus(ensureError)
        const code = getApiErrorCode(ensureError)
        if (code === 'phone_invalid') {
          // The number in the field above is what was refused — point at it
          setPhoneError(t('common.phoneRejected'))
          toast.error(t('common.phoneRejected'), { duration: 10000 })
        } else if (code === 'signing_temporarily_unavailable') {
          toast.error(t('common.signingUnavailable'), { duration: 12000 })
        } else {
          toast.error(getApiErrorMessage(ensureError) || t('common.errorSendingCode'))
          reportError({ patientNumber, errorType: 'yousign_error', errorMessage: 'SR recreate before OTP send failed', statusCode: status, step: 'step10', endpoint: '/yousign/create' })
        }
        setIsLoading(false)
        return
      }

      // Read the (possibly refreshed) SR ids AFTER ensureSignatureRequest —
      // the destructured values from the last render may be stale.
      const { yousignRequestId: srId, yousignSignerId: srSignerId } = useOnboardingStore.getState()

      // Explicitly trigger OTP send — Yousign's "automatic" send on activation does NOT
      // reliably deliver the SMS in our flow, so we must call /send-otp.
      if (srId && srSignerId) {
        try {
          await yousignService.sendOtp(srId, srSignerId)
          logger.log('[Yousign] OTP send-otp call succeeded')
        } catch (otpError: unknown) {
          const code = getApiErrorCode(otpError)
          const status = getApiErrorStatus(otpError)
          const msg = getApiErrorMessage(otpError)
          // Signature already completed — store docs and skip ahead
          if (code === 'already_signed') {
            toast.success(t('step10.alreadySigned'))
            try {
              await yousignService.storeSignedDocument(srId, patientNumber)
            } catch (storeErr) {
              logger.error('[Yousign] Failed to store signed documents:', storeErr)
            }
            onNext()
            return
          }
          // Rate limited — surface to user
          if (status === 429) {
            toast.error(msg || t('common.tooManyOtpRequests'))
            reportError({ patientNumber, errorType: 'yousign_error', errorMessage: 'OTP too many requests (send)', statusCode: 429, step: 'step10', endpoint: '/yousign/send-otp' })
            setIsLoading(false)
            return
          }
          // Stale signature request — auto-recover via the summary step
          if (status === 410 || code === 'session_expired') {
            toast.info(t('step10.sessionExpiredReset'))
            reportError({ patientNumber, errorType: 'yousign_error', errorMessage: 'Session expired — stale signature request recreated', statusCode: 410, step: 'step10', endpoint: '/yousign/send-otp' })
            recoverFromStaleSignatureRequest()
            return
          }
          // Our own server refused the request — the org requires a magic-link
          // session and this browser has none. Nothing was sent, so never fall
          // through to "code is on its way" (that is exactly how P20025 ended up
          // waiting for an SMS on 2026-09-16). The API interceptor switches the
          // app to the "open your personal link" page.
          if (code === 'patient_token_required' || status === 401) {
            setIsLoading(false)
            return
          }
          // 501/403 etc → Yousign rejected manual send; the auto-send on activation
          // should have delivered the SMS already. Continue to OTP entry.
          logger.warn('[Yousign] /send-otp not available, relying on auto-send from activation:', msg)
        }
        toast.success(t('step10.verificationSending'))
        setPhase('enter-otp')
      } else {
        toast.error(t('common.signatureNotFound'))
      }
    } catch (error) {
      logger.error('[Yousign] Failed to advance to OTP entry:', error)
      toast.error(t('common.errorSendingCode'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)
    if (!/^\d+$/.test(pastedData)) return

    const newCode = [...code]
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i]
    }
    setCode(newCode)

    const lastIndex = Math.min(pastedData.length, 5)
    inputRefs.current[lastIndex]?.focus()
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isOtpComplete) return

    setIsLoading(true)
    const otpCode = code.join('')

    try {
      // Verify OTP with Yousign and sign the document
      if (yousignRequestId && yousignSignerId) {
        // The signer IP is derived server-side from proxy headers
        const signResult = await yousignService.sign(yousignRequestId, yousignSignerId, otpCode)
        if (!signResult.success) {
          toast.error(t('common.invalidCode'))
          setIsLoading(false)
          return
        }
        logger.log('[Yousign] Signature completed:', signResult.data)
        toast.success(t('step10.documentsSignedSuccess'))

        // Store signed document and audit trail to GCS — the backend retries
        // internally while Yousign finishes processing, no fixed wait needed
        try {
          logger.log('[Yousign] Storing signed documents to GCS...')
          const storeResult = await yousignService.storeSignedDocument(yousignRequestId, patientNumber)
          if (storeResult.success) {
            logger.log('[Yousign] Documents stored to GCS:', storeResult.data)
          } else {
            logger.warn('[Yousign] Failed to store documents to GCS:', storeResult.error)
          }
        } catch (storeError) {
          // Don't block the flow if storage fails - just log it
          logger.error('[Yousign] Error storing documents to GCS:', storeError)
        }

        onNext()
      } else {
        toast.error(t('common.signatureNotFound'))
      }
    } catch (error: unknown) {
      logger.error('[Yousign] Verification failed:', error)
      const code = getApiErrorCode(error)
      const status = getApiErrorStatus(error)
      const serverMessage = getApiErrorMessage(error)

      // Signature already completed (e.g., patient came back after signing) — store docs and skip
      if (code === 'already_signed') {
        toast.success(t('step10.alreadySigned'))
        if (yousignRequestId) {
          try {
            await yousignService.storeSignedDocument(yousignRequestId, patientNumber)
          } catch (storeErr) {
            logger.error('[Yousign] Failed to store signed documents:', storeErr)
          }
        }
        onNext()
        return
      }

      // Stale signature request from a broken template config — auto-recover via
      // the summary step. The Zustand store persists form data, so the patient
      // returns to a pre-filled summary and gets a fresh signature request.
      if (status === 410 || code === 'session_expired') {
        toast.info(t('step10.sessionExpiredReset'))
        reportError({ patientNumber, errorType: 'yousign_error', errorMessage: 'Session expired — stale signature request recreated', statusCode: 410, step: 'step10', endpoint: '/yousign/sign' })
        recoverFromStaleSignatureRequest()
        return
      }

      if (status === 429) {
        toast.error(serverMessage || t('common.tooManyAttempts'))
        reportError({ patientNumber, errorType: 'yousign_error', errorMessage: serverMessage || 'OTP too many requests (verify)', statusCode: 429, step: 'step10', endpoint: '/yousign/sign' })
      } else {
        toast.error(serverMessage || t('common.invalidCodeRetry'))
        if (status && status >= 400) {
          reportError({ patientNumber, errorType: 'yousign_error', errorMessage: serverMessage || 'OTP verification failed', statusCode: status, step: 'step10', endpoint: '/yousign/sign' })
        }
      }
      // Clear the OTP inputs for retry
      setCode(Array(6).fill(''))
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setIsLoading(true)
    try {
      if (yousignRequestId && yousignSignerId) {
        try {
          await yousignService.sendOtp(yousignRequestId, yousignSignerId)
          toast.success(t('step10.newCodeSent'))
        } catch (otpError: unknown) {
          const code = getApiErrorCode(otpError)
          const status = getApiErrorStatus(otpError)
          const msg = getApiErrorMessage(otpError)
          // Signature already completed — skip ahead
          if (code === 'already_signed') {
            toast.success(t('step10.alreadySigned'))
            if (yousignRequestId) {
              try {
                await yousignService.storeSignedDocument(yousignRequestId, patientNumber)
              } catch (storeErr) {
                logger.error('[Yousign] Failed to store signed documents:', storeErr)
              }
            }
            onNext()
            return
          }
          if (status === 429) {
            toast.error(msg || t('common.tooManyOtpRequests'))
            reportError({ patientNumber, errorType: 'yousign_error', errorMessage: 'OTP too many requests (resend)', statusCode: 429, step: 'step10', endpoint: '/yousign/send-otp' })
          } else {
            // 403 means manual OTP resend not available
            toast.info(t('common.pleaseWait'))
          }
        }
      } else {
        toast.error(t('common.signatureNotFoundShort'))
      }
    } catch (error) {
      logger.error('[Yousign] Failed to resend OTP:', error)
      toast.error(t('common.errorSendingCode'))
    } finally {
      setIsLoading(false)
    }
  }

  // The number the OTP actually went to is the one embedded in the signature
  // request — show THAT, not the editable field (they can differ if the SR
  // predates a correction; older sessions may not have it persisted yet).
  const otpTargetPhone = yousignPhoneNumber || editablePhone

  // Mask all but the first 3 and last 2 digits, regardless of number length
  const maskedPhone = (() => {
    const digits = otpTargetPhone.replace(/\D/g, '')
    if (digits.length <= 5) return otpTargetPhone
    let seen = 0
    return otpTargetPhone.replace(/\d/g, (d) => {
      seen++
      return seen <= 3 || seen > digits.length - 2 ? d : '*'
    })
  })()

  // Phase 1: Confirm phone number
  if (phase === 'confirm-phone') {
    return (
      <div className="form-container">
        <form onSubmit={handleSendOtp} className="form-inner py-12">
          <h2 className="text-h2 text-foreground mb-2">
            {t('step10.title')}
          </h2>
          <p className="text-body text-foreground mb-2">
            {personalData.firstName} {personalData.lastName}
          </p>
          <p className="text-body text-muted-foreground mb-8">
            {t('step10.subtitle')}
          </p>

          <div className="space-y-6">
            <Input
              id="phone"
              type="tel"
              label={t('step10.phoneLabel')}
              placeholder={t('step10.phonePlaceholder')}
              value={editablePhone}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              error={phoneError}
              icon={<Phone className="w-5 h-5 text-muted-foreground" />}
            />

            <Checkbox
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              label={
                <span>
                  {t('step10.termsCheckbox')}{' '}
                  <a href="https://gam-medical.de/datenschutzbestimmungen/" className="text-primary underline" target="_blank" rel="noopener noreferrer">
                    {t('step10.privacyLink')}
                  </a>{' '}
                  {t('step10.andThe')}{' '}
                  <a href="https://gam-medical.de/agb/" className="text-primary underline" target="_blank" rel="noopener noreferrer">
                    {t('step10.termsLink')}
                  </a>
                </span>
              }
            />
          </div>

          <FormNavigation
            onBack={onBack}
            nextDisabled={!isValidPhone || !agreedToTerms || isLoading}
            nextLabel={isLoading ? t('common.sending') : t('step10.sendCode')}
          />
        </form>
      </div>
    )
  }

  // Phase 2: Enter OTP
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card max-w-md w-full p-8">
        <h2 className="text-h3 text-foreground text-center mb-2">
          {t('step10.enterOtp')}
        </h2>
        <p className="text-body text-foreground text-center mb-2">
          {personalData.firstName} {personalData.lastName}
        </p>
        <p className="text-body text-muted-foreground text-center mb-8">
          {t('step10.otpSentTo', { phone: maskedPhone })}
        </p>

        <form onSubmit={handleVerifyOtp}>
          <div className="flex justify-center gap-3 mb-8">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={digit}
                aria-label={`${t('step10.enterOtp')} ${index + 1}/6`}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={handleOtpPaste}
                className="w-12 h-12 text-center text-xl font-medium border border-border rounded-card focus:outline-none focus:ring-2 focus:ring-primary"
              />
            ))}
          </div>

          <div className="flex justify-between">
            <Button type="button" variant="secondary" onClick={() => setPhase('confirm-phone')}>
              {t('common.back')}
            </Button>
            <Button type="submit" disabled={!isOtpComplete || isLoading}>
              {isLoading ? t('common.checking') : t('common.confirm')}
            </Button>
          </div>
        </form>

        <p className="text-small text-muted-foreground text-center mt-6">
          {t('step10.noCodeReceived')}{' '}
          <button
            type="button"
            className="text-primary underline disabled:opacity-50 disabled:no-underline"
            onClick={() => { setResendCooldown(60); void handleResendOtp() }}
            disabled={isLoading || resendCooldown > 0}
          >
            {resendCooldown > 0 ? t('step10.resendIn', { s: resendCooldown }) : t('step10.resend')}
          </button>
        </p>
      </div>
    </div>
  )
}
